import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatPrice } from "../lib/api";
import { formatDateTimeMx, formatTimeMx } from "../lib/datetimeMx";
import { useAuth } from "../context/AuthContext";
import useTicketReprint from "../hooks/useTicketReprint";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function AdminCortesPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [sessions, setSessions] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const { reprint } = useTicketReprint();

    const loadSessions = useCallback(() => {
        return api.get("/pos/sessions", { params: { limit: 30 } }).then((r) => setSessions(r.data || []));
    }, []);

    useEffect(() => {
        loadSessions().finally(() => setLoading(false));
    }, [loadSessions]);

    const loadDetail = (id) => {
        api.get(`/pos/session/${id}/summary`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    };

    const deleteSession = async (session) => {
        const label = formatDateTimeMx(session.opened_at);
        const msg =
            `¿Eliminar el turno del ${label}?\n\n` +
            "Se borrarán todas las ventas de ese turno y el stock de los productos volverá como antes de esas ventas. " +
            "Esta acción no se puede deshacer.";
        if (!window.confirm(msg)) return;

        setDeletingId(session.id);
        try {
            const { data } = await api.delete(`/pos/session/${session.id}`);
            toast.success(
                `Turno eliminado (${data.sales_removed} venta(s), stock restaurado en ${data.products_restored} producto(s))`
            );
            if (detail?.session?.id === session.id) setDetail(null);
            await loadSessions();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return <div className="p-10 font-mono text-sm">Cargando…</div>;

    return (
        <div className="p-6 md:p-10" data-testid="admin-cortes-page">
            <div className="border-b-2 border-[var(--lz-navy)] pb-4 mb-6 flex flex-wrap justify-between gap-4">
                <div>
                    <span className="lz-section-label">Caja</span>
                    <h1 className="font-display text-4xl font-bold uppercase">Cortes de caja</h1>
                    {isSuperAdmin && (
                        <p className="text-xs text-[var(--lz-orange)] font-bold uppercase mt-1">Modo superadministrador</p>
                    )}
                </div>
                <Link to="/admin/pos" className="lz-btn lz-btn-primary">Ir al punto de venta</Link>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border-2 border-[var(--lz-navy)] bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--lz-navy)] text-white text-[10px] uppercase tracking-widest">
                                <th className="p-3 text-left">Apertura</th>
                                <th className="p-3">Estado</th>
                                <th className="p-3">Fondo</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#0f1419]/15">
                            {sessions.map((s) => (
                                <tr key={s.id} className="hover:bg-[#f3f4f6]">
                                    <td className="p-3 font-mono text-xs">{formatDateTimeMx(s.opened_at)}</td>
                                    <td className="p-3">
                                        <span className={`lz-tag ${s.status === "open" ? "bg-[var(--lz-orange)]" : "bg-[var(--lz-navy)] text-white"}`}>
                                            {s.status === "open" ? "Abierto" : "Cerrado"}
                                        </span>
                                    </td>
                                    <td className="p-3 font-mono">{formatPrice(s.opening_float)}</td>
                                    <td className="p-3 space-y-1">
                                        <button type="button" onClick={() => loadDetail(s.id)} className="block text-xs font-bold uppercase underline">
                                            Ver detalle
                                        </button>
                                        {isSuperAdmin && (
                                            <button
                                                type="button"
                                                disabled={deletingId === s.id}
                                                onClick={() => deleteSession(s)}
                                                className="flex items-center gap-1 text-xs font-bold uppercase text-red-700 hover:underline disabled:opacity-50"
                                                data-testid={`delete-session-${s.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                {deletingId === s.id ? "Eliminando…" : "Eliminar turno"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {sessions.length === 0 && (
                                <tr><td colSpan={4} className="p-8 text-center text-[var(--lz-navy)]/50">Sin turnos registrados</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="border-2 border-[var(--lz-navy)] bg-white p-5 min-h-[200px]">
                    {!detail && <p className="text-sm text-[var(--lz-navy)]/50">Selecciona un turno para ver el resumen.</p>}
                    {detail && (
                        <div>
                            <div className="flex flex-wrap justify-between gap-2 mb-4">
                                <h2 className="font-display text-xl font-bold uppercase">Resumen del turno</h2>
                                {isSuperAdmin && (
                                    <button
                                        type="button"
                                        disabled={deletingId === detail.session.id}
                                        onClick={() => deleteSession(detail.session)}
                                        className="lz-btn lz-btn-outline text-xs text-red-700 border-red-700"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        {deletingId === detail.session.id ? "Eliminando…" : "Eliminar turno"}
                                    </button>
                                )}
                            </div>
                            <dl className="space-y-2 text-sm font-mono mb-4">
                                <div className="flex justify-between"><dt>Cajero</dt><dd>{detail.session.opened_by}</dd></div>
                                <div className="flex justify-between"><dt>Fondo inicial</dt><dd>{formatPrice(detail.opening_float)}</dd></div>
                                <div className="flex justify-between"><dt>Ventas</dt><dd>{detail.sales_count} · {formatPrice(detail.sales_total)}</dd></div>
                                <div className="flex justify-between font-bold"><dt>Efectivo esperado</dt><dd>{formatPrice(detail.expected_cash)}</dd></div>
                                <div className="flex justify-between"><dt>Ajustes</dt><dd>-{formatPrice(detail.deductions_total || 0)}</dd></div>
                                <div className="flex justify-between font-bold"><dt>Esperado ajustado</dt><dd>{formatPrice(detail.expected_cash_adjusted || detail.expected_cash)}</dd></div>
                                {detail.session.status === "closed" && (
                                    <>
                                        <div className="flex justify-between"><dt>Contado</dt><dd>{formatPrice(detail.session.closing_cash_counted)}</dd></div>
                                        <div className="flex justify-between"><dt>Diferencia</dt><dd>{formatPrice(detail.session.cash_difference)}</dd></div>
                                    </>
                                )}
                            </dl>
                            {!!(detail.deductions || []).length && (
                                <div className="mb-4 border border-[var(--lz-navy)]/15 p-2">
                                    <h3 className="text-xs font-bold uppercase tracking-widest mb-2">Ajustes registrados</h3>
                                    <ul className="space-y-1 text-xs font-mono">
                                        {(detail.deductions || []).map((d, idx) => (
                                            <li key={`${d.concept}-${idx}`} className="flex justify-between">
                                                <span>{d.concept}</span>
                                                <span>-{formatPrice(d.amount)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <h3 className="text-xs font-bold uppercase tracking-widest mb-2">Ventas</h3>
                            <ul className="space-y-2 max-h-60 overflow-auto text-xs">
                                {detail.sales.map((sale) => (
                                    <li key={sale.id} className="border border-[var(--lz-navy)]/15 p-2">
                                        <div className="flex flex-wrap justify-between gap-2 font-bold items-center">
                                            <span>{formatTimeMx(sale.created_at)}</span>
                                            <span className="font-mono text-xs">{sale.ticket_number || "—"}</span>
                                            <span>{formatPrice(sale.total)}</span>
                                            <button
                                                type="button"
                                                className="lz-btn lz-btn-outline text-[10px]"
                                                onClick={() => reprint(sale)}
                                            >
                                                Reimprimir
                                            </button>
                                        </div>
                                        <div className="text-[var(--lz-navy)]/60 mt-1">
                                            {sale.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
